import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown, ArrowRight, ArrowUp, Loader2, TrendingDown, TrendingUp, X,
} from "lucide-react";

// Format a Finviz change-pct cell. Returns "" rather than "—" so the mobile
// compact row stays empty when data is missing (avoids double-dash).
function formatChange(pct, isUp) {
  if (pct == null) return "";
  return `${isUp ? "+" : ""}${pct.toFixed(2)}%`;
}

/**
 * Sortable Finviz results table for the Stock Screener page.
 *
 * Owns the client-side sort state (the parent already passes already-sorted
 * data from the server; this layer lets the user re-sort without refetching).
 */
export default function ScreenerTable({ items, loading, error, cached }) {
  const [clientSort, setClientSort] = useState(null);

  const sortedItems = useMemo(() => {
    if (!clientSort) return items;
    const { col, dir } = clientSort;
    const mult = dir === "asc" ? 1 : -1;
    const get = (i) => {
      if (col === "market_cap") return i.market_cap_num ?? -Infinity;
      if (col === "volume") return i.volume_num ?? -Infinity;
      if (col === "pe") {
        const v = parseFloat(i.pe);
        return Number.isFinite(v) ? v : Infinity * mult;
      }
      if (col === "price") return i.price ?? -Infinity;
      if (col === "change") return i.change_pct ?? -Infinity;
      if (col === "symbol") return (i.symbol || "").toLowerCase();
      return 0;
    };
    return [...items].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
  }, [items, clientSort]);

  const toggleClientSort = (col) => {
    setClientSort((s) => {
      if (!s || s.col !== col) return { col, dir: "desc" };
      if (s.dir === "desc") return { col, dir: "asc" };
      return null; // third click clears
    });
  };

  const SortHeader = ({ col, label, align = "left" }) => {
    const active = clientSort?.col === col;
    const Icon = active ? (clientSort.dir === "asc" ? ArrowUp : ArrowDown) : null;
    return (
      <button
        onClick={() => toggleClientSort(col)}
        data-testid={`screener-sort-${col}`}
        className={`flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold transition-soft ${
          active ? "text-[#2A2F2B]" : "text-[#6B7068] hover:text-[#2A2F2B]"
        } ${align === "right" ? "justify-end w-full" : ""}`}
      >
        {align === "right" && Icon && <Icon className="w-3 h-3" strokeWidth={2} />}
        {label}
        {align !== "right" && Icon && <Icon className="w-3 h-3" strokeWidth={2} />}
      </button>
    );
  };

  return (
    <section data-testid="screener-results">
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="text-sm text-[#6B7068]">
          {loading
            ? "Searching the market..."
            : `${items.length} match${items.length === 1 ? "" : "es"}`}
          {cached && !loading && (
            <span
              className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#D18E63]"
              data-testid="screener-cached-badge"
            >
              · cached
            </span>
          )}
        </div>
      </div>

      {error && (
        <div
          className="rounded-2xl bg-[#C66B59]/10 border border-[#C66B59]/30 p-4 text-sm text-[#C66B59] mb-4 flex items-center gap-2"
          data-testid="screener-error"
        >
          <X className="w-4 h-4" strokeWidth={1.5} /> {error}
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#EAE6E1] overflow-hidden">
        <div className="hidden md:grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_90px_70px_90px_80px] gap-4 px-5 py-3 border-b border-[#EAE6E1]">
          <SortHeader col="symbol" label="Ticker" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B7068]">Company</div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B7068]">Sector</div>
          <SortHeader col="market_cap" label="Mkt Cap" align="right" />
          <SortHeader col="pe" label="P/E" align="right" />
          <SortHeader col="price" label="Price" align="right" />
          <SortHeader col="change" label="1D" align="right" />
        </div>
        {loading && items.length === 0 && (
          <div className="px-5 py-12 flex items-center justify-center text-sm text-[#6B7068]" data-testid="screener-loading">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading from Finviz...
          </div>
        )}
        {!loading && items.length === 0 && !error && (
          <div className="px-5 py-12 text-center text-sm text-[#6B7068]" data-testid="screener-empty">
            No stocks match those filters. Try widening the criteria.
          </div>
        )}
        {sortedItems.map((it) => {
          const up = (it.change_pct ?? 0) >= 0;
          const trendColor = up ? "#5C8D73" : "#C66B59";
          const changeLabel = formatChange(it.change_pct, up);
          return (
            <Link
              key={it.symbol}
              to={`/asset/stock/${it.symbol}`}
              data-testid={`screener-row-${it.symbol}`}
              className="grid grid-cols-2 md:grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_90px_70px_90px_80px] gap-4 px-5 py-3 border-b border-[#EAE6E1] last:border-0 hover:bg-[#F9F8F6] transition-soft items-center group"
            >
              <div className="font-mono-ticker font-semibold text-sm text-[#2A2F2B] flex items-center gap-1">
                {it.symbol}
                <ArrowRight className="w-3 h-3 text-[#6B7068] opacity-0 group-hover:opacity-100 transition-soft" strokeWidth={1.5} />
              </div>
              <div className="text-sm text-[#2A2F2B] truncate" title={it.name}>
                {it.name}
              </div>
              <div className="hidden md:block text-xs text-[#6B7068] truncate" title={it.industry}>
                {it.sector}
              </div>
              <div className="hidden md:block text-right font-mono-ticker text-sm text-[#2A2F2B]">
                {it.market_cap || "—"}
              </div>
              <div className="hidden md:block text-right font-mono-ticker text-sm text-[#2A2F2B]">
                {it.pe || "—"}
              </div>
              <div className="hidden md:block text-right font-mono-ticker text-sm text-[#2A2F2B]">
                {it.price != null ? `$${it.price.toFixed(2)}` : "—"}
              </div>
              <div className="hidden md:flex items-center justify-end gap-0.5 font-mono-ticker text-sm font-semibold" style={{ color: trendColor }}>
                {it.change_pct != null && (up ? <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> : <TrendingDown className="w-3 h-3" strokeWidth={2.5} />)}
                {changeLabel || "—"}
              </div>
              {/* mobile compact row */}
              <div className="md:hidden text-right text-xs text-[#6B7068] flex items-center justify-end gap-2">
                <span>{it.market_cap}</span>
                <span style={{ color: trendColor }} className="font-mono-ticker font-semibold">
                  {changeLabel}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
      <p className="text-[10px] text-[#6B7068] mt-3">
        Data via Finviz. Educational purposes only — not investment advice.
      </p>
    </section>
  );
}
